import { formatDateToApiIso } from "@/src/utils/formatDateToApiIso";
import { formatOfficeAccountingGeneralStatusForApi } from "@/src/constants/officeAccountingTableConfig";

export type OfficeAccountingUpdateTarget = "accounting" | "applicant" | "task";

export interface OfficeAccountingColumnUpdateMapping {
  target: OfficeAccountingUpdateTarget;
  apiField: string;
  isDate?: boolean;
}

/** Columns that already have edit popups in Office Accounting table. */
const COLUMN_UPDATE_MAP: Record<string, OfficeAccountingColumnUpdateMapping> = {
  country: { target: "applicant", apiField: "country" },
  dateOfBirth: { target: "applicant", apiField: "birth_date", isDate: true },
  passportNo: { target: "applicant", apiField: "passport_number" },
  passportIssued: { target: "applicant", apiField: "passport_issued", isDate: true },
  validPassport: { target: "applicant", apiField: "valid_passport", isDate: true },
  phone: { target: "applicant", apiField: "phone" },
  email: { target: "applicant", apiField: "email" },
  preparatoryDepartment: { target: "applicant", apiField: "preparatory_department" },
  certificationEqualization: { target: "applicant", apiField: "certification_equalization" },

  admissionSemester: { target: "task", apiField: "admission_semester" },
  intake: { target: "task", apiField: "intake" },
  tuitionFee: { target: "task", apiField: "tuition_fee" },

  managersEmail: { target: "accounting", apiField: "managers_email" },
  agentsEmail: { target: "accounting", apiField: "agents_email" },
  currentSemester: { target: "accounting", apiField: "current_semester" },
  generalStatus: { target: "accounting", apiField: "general_status" },
  invoiceNumber: { target: "accounting", apiField: "invoice_number" },
  statusAdmissionPayment: { target: "accounting", apiField: "status_admission_payment" },
  statusAl: { target: "accounting", apiField: "status_al" },
  statusEmail: { target: "accounting", apiField: "status_email" },
  statusEdbo: { target: "accounting", apiField: "status_edbo" },
  statusInvitationFee: { target: "accounting", apiField: "status_invitation_fee" },
  balanceStatusUsd: { target: "accounting", apiField: "balance_status_usd" },
  statusMissedClasses: { target: "accounting", apiField: "status_missed_classes" },
  costFirstSemester: { target: "accounting", apiField: "cost_first_semester" },
  costSecondSemester: { target: "accounting", apiField: "cost_second_semester" },
  costThirdSemester: { target: "accounting", apiField: "cost_third_semester" },
  costFouthSemester: { target: "accounting", apiField: "cost_fourth_semester" },
  costFifthSemester: { target: "accounting", apiField: "cost_fifth_semester" },
  costSixthSemester: { target: "accounting", apiField: "cost_sixth_semester" },
  costSeventhSemester: { target: "accounting", apiField: "cost_seventh_semester" },
  costEighthSemester: { target: "accounting", apiField: "cost_eighth_semester" },
  costNinthSemester: { target: "accounting", apiField: "cost_ninth_semester" },
  costTenthSemester: { target: "accounting", apiField: "cost_tenth_semester" },
  costEleventhSemester: { target: "accounting", apiField: "cost_eleventh_semester" },
  costTwelfthSemester: { target: "accounting", apiField: "cost_twelfth_semester" },
};

export const mapOfficeAccountingColumnUpdate = (columnId: string): OfficeAccountingColumnUpdateMapping | null =>
  COLUMN_UPDATE_MAP[columnId] ?? null;

export const formatOfficeAccountingUpdateValue = (
  mapping: OfficeAccountingColumnUpdateMapping,
  value: string,
): string => {
  if (mapping.apiField === "general_status") {
    return formatOfficeAccountingGeneralStatusForApi(value);
  }
  return mapping.isDate ? formatDateToApiIso(value) || value.trim() : value;
};
